import React, { useCallback } from 'react';
import Editor from '../../Editor';
import { EditorProps } from '../../types';
import FormatBubbleMenu from '../../../menus/FormatBubbleMenu';
import FullscreenEditor from '../Fullscreen/Fullscreen';
import EverythingMenu from '../../../menus/EverythingMenu/EverythingMenu';
import cn from 'classnames';
import UniversalMobileMenu from '../../../menus/UniversalMobileMenu';
import { extConfig, extConfigFullscreen } from './config';
import PluginInsertMenu from '../../../menus/PluginInsertMenu';
import FullscreenIcon from '@wix/da-ds/pkg/Icons/24x24/FullView';
import { useTranslation } from 'react-i18next';
import Viewer from '../../../Viewer/presets/Gallection';
import { isTouch } from '../../../utils/browser';
import isTextEmpty from '../../../utils/isEmpty';
import useIsMobileOrTouch from '../../../utils/useIsMobileOrTouch';
import EditorIconButton from '../../../menus/_partials/EditorIconButton';
import { useEditorContext } from '../../Context';

import s from './Gallection.scss';
import presetViewerStyles from '../../../Viewer/presets/Gallection/Gallection.scss';

export interface Props
  extends Omit<EditorProps, 'extensionConfig' | 'presetId'> {
  compact?: boolean;
}

const GallectionEditor: React.FC<Props> = ({ compact, ...props }) => {
  const shouldUseMobileLayout = useIsMobileOrTouch();
  const { t } = useTranslation('editor');
  const editor = useEditorContext();

  const focusEditor = useCallback(
    e => {
      if (
        e.target &&
        e.target.getAttribute('data-wrapper') &&
        !editor?.isFocused
      ) {
        editor?.commands.focus('end');
      }
    },
    [editor?.commands, editor?.isFocused]
  );

  if (shouldUseMobileLayout) {
    return (
      <FullscreenEditor
        modalProps={{
          heading: t('preset.gallection.fullscreen.heading'),
          contentClassName: s['background'],
        }}
      >
        {(currentContent, isFullscreen, toggleFs, scrollRef) => {
          return (
            <div
              className={cn(
                s['wrapper'],
                isFullscreen && s['fullscreen'],
                shouldUseMobileLayout && s['mobile']
              )}
            >
              {!isFullscreen && (
                <div className={s['viewer']} onClick={toggleFs}>
                  {isTextEmpty(currentContent?.doc ?? props.initialContent) && (
                    <p className={s['fake-placeholder']}>
                      {t('preset.gallection.placeholder')}
                    </p>
                  )}
                  <Viewer
                    className={s['viewer-inner']}
                    contentJson={currentContent?.doc ?? props.initialContent}
                    {...props}
                  />
                </div>
              )}

              {isFullscreen && (
                <div className={cn(s['stuck'])}>
                  <UniversalMobileMenu />
                </div>
              )}

              {isFullscreen && (
                <div className={cn(s['fixed-wrapper'])}>
                  <Editor
                    {...props}
                    withinModal
                    presetId="gallection"
                    className={s['root']}
                    extensionConfig={
                      isFullscreen ? extConfigFullscreen : extConfig
                    }
                    initialContent={currentContent?.doc ?? props.initialContent}
                    initialSelection={currentContent?.selection}
                    scrollContainerRef={scrollRef}
                    autoScrollWhenWithin={50}
                    placeholder={t('preset.gallection.placeholder')}
                  >
                    {(_, scrollContainer) => (
                      <>
                        {!isFullscreen && (
                          <FormatBubbleMenu
                            scrollContainerRef={scrollContainer}
                          />
                        )}
                      </>
                    )}
                  </Editor>
                </div>
              )}
            </div>
          );
        }}
      </FullscreenEditor>
    );
  }

  return (
    <FullscreenEditor
      modalProps={{
        heading: t('preset.gallection.fullscreen.heading'),
        contentClassName: s['background'],
      }}
    >
      {(currentContent, isFullscreen, toggleFs, scrollRef) => {
        return (
          <>
            {isFullscreen && (
              <div className={cn(s['stuck'])}>
                <EverythingMenu wrapping={false} />
              </div>
            )}
            <div
              className={cn(
                isFullscreen && s['fixed-wrapper'],
                compact && s['compact']
              )}
              data-wrapper="1"
              onClick={focusEditor}
            >
              <Editor
                {...props}
                withinModal
                presetId="gallection"
                className={cn(
                  s['editor'],
                  presetViewerStyles['root'],
                  !isFullscreen && s['in-form']
                )}
                extensionConfig={isFullscreen ? extConfigFullscreen : extConfig}
                initialContent={currentContent?.doc ?? props.initialContent}
                initialSelection={
                  isFullscreen && !isTouch() && currentContent?.selection
                }
                selfScrollContainer={!isFullscreen}
                scrollContainerRef={isFullscreen ? scrollRef : undefined}
                autoScrollWhenWithin={50}
                placeholder={t('preset.gallection.placeholder')}
              >
                {(_, scrollContainer) => (
                  <>
                    {!isFullscreen && (
                      <FormatBubbleMenu scrollContainerRef={scrollContainer} />
                    )}
                  </>
                )}
              </Editor>
            </div>

            {!isFullscreen && (
              <div className={cn(s['toolbar'])}>
                <PluginInsertMenu className={s['insert-menu']} />
                <EditorIconButton
                  featureId="action:fullscreen"
                  icon={FullscreenIcon}
                  onClick={toggleFs}
                  aria-label={t('button.fullscreen')}
                  tooltipText={t('button.fullscreen')}
                />
              </div>
            )}
          </>
        );
      }}
    </FullscreenEditor>
  );
};
GallectionEditor.displayName = 'GallectionEditor';

export default GallectionEditor;
